import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";

const AppDialogContext = createContext(null);

const INITIAL_DIALOG_STATE = {
  isOpen: false,
  title: "",
  message: "",
  type: "info", // info | success | warning | error | confirm | prompt
  confirmText: "OK",
  cancelText: "Cancel",
  showCancel: false,
  resolve: null,

  // prompt support
  showInput: false,
  inputValue: "",
  inputPlaceholder: "",
  inputLabel: "",
  inputType: "text",
  validateInput: null,
  inputError: "",
};

export function AppDialogProvider({ children }) {
  const [dialog, setDialog] = useState(INITIAL_DIALOG_STATE);
  const inputRef = useRef(null);

  const resetDialog = useCallback(() => {
    setDialog(INITIAL_DIALOG_STATE);
  }, []);

  const closeDialog = useCallback(
    (result) => {
      if (dialog.resolve) {
        if (dialog.showInput) {
          if (result === true) {
            dialog.resolve(dialog.inputValue);
          } else {
            dialog.resolve(null);
          }
        } else {
          dialog.resolve(result);
        }
      }

      resetDialog();
    },
    [dialog, resetDialog]
  );

  const showAlert = useCallback((options = {}) => {
    return new Promise((resolve) => {
      setDialog({
        ...INITIAL_DIALOG_STATE,
        isOpen: true,
        title: options.title || getDefaultTitle(options.type || "info"),
        message: options.message || "",
        type: options.type || "info",
        confirmText: options.confirmText || "OK",
        cancelText: options.cancelText || "Cancel",
        showCancel: false,
        resolve,
      });
    });
  }, []);

  const showConfirm = useCallback((options = {}) => {
    return new Promise((resolve) => {
      setDialog({
        ...INITIAL_DIALOG_STATE,
        isOpen: true,
        title: options.title || "Please Confirm",
        message: options.message || "",
        type: options.type || "confirm",
        confirmText: options.confirmText || "Continue",
        cancelText: options.cancelText || "Cancel",
        showCancel: true,
        resolve,
      });
    });
  }, []);

  const showPrompt = useCallback((options = {}) => {
    return new Promise((resolve) => {
      setDialog({
        ...INITIAL_DIALOG_STATE,
        isOpen: true,
        title: options.title || "Enter Value",
        message: options.message || "",
        type: options.type || "prompt",
        confirmText: options.confirmText || "OK",
        cancelText: options.cancelText || "Cancel",
        showCancel: true,
        resolve,

        showInput: true,
        inputValue: options.defaultValue || "",
        inputPlaceholder: options.placeholder || "",
        inputLabel: options.inputLabel || "",
        inputType: options.inputType || "text",
        validateInput:
          typeof options.validateInput === "function" ? options.validateInput : null,
        inputError: "",
      });

      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          inputRef.current.select?.();
        }
      }, 0);
    });
  }, []);

  const handleConfirmClick = useCallback(() => {
    if (dialog.showInput) {
      const value = String(dialog.inputValue ?? "");

      if (typeof dialog.validateInput === "function") {
        const validationResult = dialog.validateInput(value);

        if (validationResult !== true) {
          setDialog((prev) => ({
            ...prev,
            inputError:
              typeof validationResult === "string"
                ? validationResult
                : "Please enter a valid value.",
          }));
          return;
        }
      }
    }

    closeDialog(true);
  }, [dialog, closeDialog]);

  const handleCancelClick = useCallback(() => {
    closeDialog(false);
  }, [closeDialog]);

  const handleInputKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleConfirmClick();
      }
    },
    [handleConfirmClick]
  );

  const value = useMemo(() => {
    return {
      alert: showAlert,
      confirm: showConfirm,
      prompt: showPrompt,
      success: (message, title = "Success") =>
        showAlert({ title, message, type: "success" }),
      error: (message, title = "Something Went Wrong") =>
        showAlert({ title, message, type: "error" }),
      warning: (message, title = "Warning") =>
        showAlert({ title, message, type: "warning" }),
      info: (message, title = "Notice") =>
        showAlert({ title, message, type: "info" }),
    };
  }, [showAlert, showConfirm, showPrompt]);

  return (
    <AppDialogContext.Provider value={value}>
      {children}

      <Modal
        isOpen={dialog.isOpen}
        centered
        backdrop="static"
        keyboard={false}
        contentClassName="app-dialog-modal"
      >
        <ModalHeader
          toggle={handleCancelClick}
          className="border-0 pb-0 app-dialog-header"
        >
          <div className="app-dialog-title-wrap">
            <div className={`app-dialog-icon app-dialog-icon-${dialog.type}`}>
              <i className={getDialogIcon(dialog.type)} />
            </div>
            <div>
              <div className="app-dialog-title">{dialog.title}</div>
            </div>
          </div>
        </ModalHeader>

        <ModalBody className="pt-3 app-dialog-body">
          <div className="app-dialog-message">{dialog.message}</div>

          {dialog.showInput && (
            <div className="mt-3">
              {dialog.inputLabel ? (
                <label className="app-dialog-input-label">{dialog.inputLabel}</label>
              ) : null}

              <Input
                innerRef={inputRef}
                type={dialog.inputType}
                value={dialog.inputValue}
                placeholder={dialog.inputPlaceholder}
                onChange={(e) =>
                  setDialog((prev) => ({
                    ...prev,
                    inputValue: e.target.value,
                    inputError: "",
                  }))
                }
                onKeyDown={handleInputKeyDown}
                className={`app-dialog-input ${
                  dialog.inputError ? "app-dialog-input-invalid" : ""
                }`}
              />

              {dialog.inputError ? (
                <div className="app-dialog-input-error mt-2">{dialog.inputError}</div>
              ) : null}
            </div>
          )}
        </ModalBody>

        <ModalFooter className="border-0 pt-0 app-dialog-footer">
          {dialog.showCancel && (
            <Button
              color="light"
              className="app-dialog-btn app-dialog-btn-secondary"
              onClick={handleCancelClick}
            >
              {dialog.cancelText}
            </Button>
          )}

          <Button
            color={getConfirmButtonColor(dialog.type)}
            className="app-dialog-btn"
            onClick={handleConfirmClick}
          >
            {dialog.confirmText}
          </Button>
        </ModalFooter>
      </Modal>

      <style>{`
        .app-dialog-modal {
          border: 0;
          border-radius: 1.15rem;
          overflow: hidden;
          box-shadow: 0 24px 60px rgba(0, 0, 0, 0.18);
        }

        .app-dialog-header,
        .app-dialog-body,
        .app-dialog-footer {
          padding-left: 1.35rem;
          padding-right: 1.35rem;
        }

        .app-dialog-title-wrap {
          display: flex;
          align-items: center;
          gap: 0.9rem;
        }

        .app-dialog-icon {
          width: 46px;
          height: 46px;
          border-radius: 0.95rem;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          font-size: 1.15rem;
        }

        .app-dialog-icon-info,
        .app-dialog-icon-confirm,
        .app-dialog-icon-prompt {
          background: rgba(13, 110, 253, 0.12);
          color: #0d6efd;
        }

        .app-dialog-icon-success {
          background: rgba(25, 135, 84, 0.14);
          color: #198754;
        }

        .app-dialog-icon-warning {
          background: rgba(255, 193, 7, 0.2);
          color: #997404;
        }

        .app-dialog-icon-error {
          background: rgba(220, 53, 69, 0.14);
          color: #dc3545;
        }

        .app-dialog-title {
          font-size: 1.05rem;
          font-weight: 700;
          color: #212529;
          line-height: 1.2;
        }

        .app-dialog-message {
          font-size: 0.96rem;
          line-height: 1.6;
          color: #495057;
          white-space: pre-line;
        }

        .app-dialog-footer {
          display: flex;
          gap: 0.65rem;
        }

        .app-dialog-btn {
          border-radius: 0.75rem;
          font-weight: 600;
          min-width: 110px;
        }

        .app-dialog-btn-secondary {
          border: 1px solid #e9ebec;
        }

        .app-dialog-input-label {
          display: block;
          margin-bottom: 0.45rem;
          font-size: 0.86rem;
          font-weight: 600;
          color: #495057;
        }

        .app-dialog-input {
          border-radius: 0.75rem;
          min-height: 44px;
          border: 1px solid #dbe4ee;
          box-shadow: none;
        }

        .app-dialog-input:focus {
          border-color: #86b7fe;
          box-shadow: 0 0 0 0.18rem rgba(13, 110, 253, 0.12);
        }

        .app-dialog-input-invalid {
          border-color: #dc3545;
        }

        .app-dialog-input-error {
          color: #dc3545;
          font-size: 0.84rem;
          font-weight: 500;
        }
      `}</style>
    </AppDialogContext.Provider>
  );
}

export function useAppDialog() {
  const context = useContext(AppDialogContext);

  if (!context) {
    throw new Error("useAppDialog must be used inside AppDialogProvider");
  }

  return context;
}

function getDefaultTitle(type) {
  switch (type) {
    case "success":
      return "Success";
    case "warning":
      return "Warning";
    case "error":
      return "Something Went Wrong";
    case "confirm":
      return "Please Confirm";
    case "prompt":
      return "Enter Value";
    default:
      return "Notice";
  }
}

function getDialogIcon(type) {
  switch (type) {
    case "success":
      return "ri-checkbox-circle-fill";
    case "warning":
      return "ri-error-warning-fill";
    case "error":
      return "ri-close-circle-fill";
    case "confirm":
      return "ri-question-fill";
    case "prompt":
      return "ri-edit-circle-fill";
    default:
      return "ri-information-fill";
  }
}

function getConfirmButtonColor(type) {
  switch (type) {
    case "success":
      return "success";
    case "warning":
      return "warning";
    case "error":
      return "danger";
    case "confirm":
      return "primary";
    case "prompt":
      return "primary";
    default:
      return "primary";
  }
}